import React from 'react';
import { BarChartConfig, GraphRenderer, GraphCallbacks } from '../../../../types/graph-builder.types';
import { DataSet, ColumnId } from '../../../../types';
import { BarChart } from '../../BarChart';
import { getColumnData, getColumnById } from '../../../utils/column-utils';
import logger from '../../../utils/logger';

/**
 * BarChartのレンダラー実装
 */
export const barChartRenderer: GraphRenderer<BarChartConfig> = {
  validate(config: BarChartConfig, dataset: DataSet): string | null {
    // データセットの検証
    if (!dataset || !dataset.schema || !dataset.schema.columns) {
      return 'Invalid dataset';
    }
    
    // カテゴリカラムの検証
    if (!config.categoryColumn) {
      return 'Please select a category column';
    }
    
    const categoryColumn = getColumnById(dataset, config.categoryColumn);
    if (!categoryColumn) {
      return 'Selected category column not found';
    }
    
    // 値カラムの検証（複数対応）
    if (!config.valueColumns || config.valueColumns.length === 0) {
      return 'Please select at least one value column';
    }
    
    // 各値カラムの検証
    for (const valueCol of config.valueColumns) {
      // $countの場合は特別な処理
      if (valueCol !== '$count') {
        const valueColumn = getColumnById(dataset, valueCol as ColumnId);
        if (!valueColumn) {
          return `Selected value column not found: ${valueCol}`;
        }

        // 値カラムが数値型であることを確認
        if (valueColumn.type !== 'int64' && valueColumn.type !== 'float64') {
          return `Value column must be numeric (int64 or float64): ${valueColumn.name}`;
        }
      }
    }
    
    return null;
  },
  
  render(config: BarChartConfig, dataset: DataSet, callbacks: GraphCallbacks): React.ReactElement {
    const categoryColumnData = getColumnData(dataset, config.categoryColumn);
    const categoryColumn = getColumnById(dataset, config.categoryColumn);
    
    if (!categoryColumnData || !categoryColumn) {
      return <div>Error: Category column data not found</div>;
    }
    
    // 複数の値系列を処理
    const seriesData: { name: string; values: number[] }[] = [];
    const seriesNames: string[] = [];
    const categories: string[] = [];
    const originalDataMap = new Map<string, number[]>(); // カテゴリごとのインデックス
    
    // カテゴリごとのインデックスを収集
    categoryColumnData.forEach((categoryValue, index) => {
      const category = String(categoryValue ?? 'N/A');
      if (!originalDataMap.has(category)) {
        originalDataMap.set(category, []);
        categories.push(category);
      }
      originalDataMap.get(category)!.push(index);
    });
    
    // 各値列を処理
    for (let seriesIdx = 0; seriesIdx < config.valueColumns.length; seriesIdx++) {
      const valueCol = config.valueColumns[seriesIdx];
      const valueKey = valueCol === '$count' ? valueCol : String(valueCol);

      let seriesName = '';
      const seriesValues: number[] = [];

      if (valueCol === '$count') {
        seriesName = 'Count';

        // カテゴリごとにカウント
        categories.forEach(category => {
          const indices = originalDataMap.get(category) || [];
          seriesValues.push(indices.length);
        });

      } else {
        // 通常の値列
        const valueColumn = getColumnById(dataset, valueCol as ColumnId);
        const valueColumnData = getColumnData(dataset, valueCol as ColumnId);
        
        if (!valueColumn || !valueColumnData) {
          continue;
        }
        
        seriesName = valueColumn.name;
        const aggregation = config.aggregations?.[valueKey] || 'average';
        
        // カテゴリごとに集計
        categories.forEach(category => {
          const indices = originalDataMap.get(category) || [];
          let sum = 0;
          let count = 0;
          let min = Infinity;
          let max = -Infinity;
          
          indices.forEach(index => {
            const value = valueColumnData[index];
            if (typeof value === 'number' && !isNaN(value)) {
              sum += value;
              count++;
              min = Math.min(min, value);
              max = Math.max(max, value);
            }
          });
          
          let finalValue = 0;
          if (count > 0) {
            switch (aggregation) {
              case 'sum':
                finalValue = sum;
                break;
              case 'average':
                finalValue = sum / count;
                break;
              case 'min':
                finalValue = min;
                break;
              case 'max':
                finalValue = max;
                break;
              default:
                finalValue = sum / count;
            }
          }
          
          seriesValues.push(finalValue);
        });
      }
      
      seriesData.push({ name: seriesName, values: seriesValues });
      seriesNames.push(seriesName);
    }

    // カテゴリをソート（最初の値系列を基準にする）
    const sortOrder = config.sortOrder || 'alphabetical';
    logger.info('[BarChartRenderer] Sorting categories', { sortOrder, categoryCount: categories.length });

    // カテゴリとインデックスのペアを作成
    const categoryIndices = categories.map((cat, idx) => ({ category: cat, originalIndex: idx }));

    if (sortOrder === 'alphabetical') {
      categoryIndices.sort((a, b) => a.category.localeCompare(b.category));
    } else if (sortOrder === 'frequency-desc' || sortOrder === 'frequency-asc') {
      // 最初の値系列の値でソート
      if (seriesData.length > 0) {
        const firstSeriesValues = seriesData[0].values;
        categoryIndices.sort((a, b) => {
          const valueA = firstSeriesValues[a.originalIndex] || 0;
          const valueB = firstSeriesValues[b.originalIndex] || 0;
          return sortOrder === 'frequency-desc' ? valueB - valueA : valueA - valueB;
        });
      }
    }

    // ソート後のカテゴリと値の配列を作成
    let sortedCategories = categoryIndices.map(item => item.category);
    let sortedSeriesData = seriesData.map(series => ({
      name: series.name,
      values: categoryIndices.map(item => series.values[item.originalIndex])
    }));

    // topNで絞り込む
    if (config.topN && config.topN > 0 && config.topN < sortedCategories.length) {
      sortedCategories = sortedCategories.slice(0, config.topN);
      sortedSeriesData = sortedSeriesData.map(series => ({
        name: series.name,
        values: series.values.slice(0, config.topN)
      }));
      logger.info('[BarChartRenderer] Filtered to top N', { topN: config.topN, remainingCategories: sortedCategories.length });
    }

    // 元の配列を更新
    categories.length = 0;
    categories.push(...sortedCategories);
    seriesData.length = 0;
    seriesData.push(...sortedSeriesData);

    logger.info('[BarChartRenderer] Categories after sort', {
      categories: categories.slice(0, 10),
      firstSeriesValues: seriesData[0]?.values.slice(0, 10)
    });

    // originalDataを構築（複数系列対応）
    const originalData: { category: string; seriesIndex: number; value: number; index: number }[] = [];
    categories.forEach((category, catIdx) => {
      const indices = originalDataMap.get(category) || [];
      indices.forEach(index => {
        seriesData.forEach((series, seriesIdx) => {
          originalData.push({
            category,
            seriesIndex: seriesIdx,
            value: series.values[catIdx],
            index
          });
        });
      });
    });
    
    // 値の配列を作成（単一系列の場合は1次元配列、複数系列の場合は2次元配列）
    const values = seriesData.length === 1 
      ? seriesData[0].values 
      : seriesData.map(s => s.values);
    
    const handleBarClick = callbacks.onElementSelect
      ? (_category: string, _seriesIndex: number, _value: number, dataIndices: number[], isAdditive?: boolean) => {
          callbacks.onElementSelect!(dataIndices, isAdditive || false);
        }
      : undefined;
    
    // タイトルを生成
    let title = config.title;
    if (!title) {
      if (seriesNames.length === 1) {
        title = `${seriesNames[0]} by ${categoryColumn.name}`;
      } else {
        title = `${seriesNames.join(', ')} by ${categoryColumn.name}`;
      }
    }
    
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <BarChart
          categories={categories}
          values={values}
          seriesNames={seriesNames.length > 1 ? seriesNames : undefined}
          title={title}
          orientation={config.orientation || 'vertical'}
          showValues={config.showValues || false}
          stackMode={config.stackMode || 'grouped'}
          onBarClick={handleBarClick}
          originalData={originalData}
          selectedIndices={callbacks.selectedRows}
          width={undefined}
          height={undefined}
          barColor={['#4f81bd', '#c0504d', '#9bbb59', '#8064a2', '#4bacc6', '#f79646', '#646464']}
          selectedBarColor={['#e6a66b', '#f4b3b3', '#d4e4a1', '#c3b1d4', '#a6d8e8', '#ffc8a3', '#b3b3b3']}
          selectedBarStrokeColor={['#d4935a', '#e89292', '#b9d473', '#9d86bf', '#7fc1d6', '#ffb075', '#8c8c8c']}
        />
      </div>
    );
  }
};